import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PawPrint, Save, X, Syringe } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddAnimal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [formData, setFormData] = useState({
    photo: '',
    species: 'Dog',
    name: '',
    gender: 'Male',
    colour: '',
    area: '',
    sterilized: false,
    vaccinated: false,
    temperament: 'Friendly',
    remarks: '',
    ownerTower: '',
    ownerFlatNo: '',
    ownerName: '',
    ownerContact: '',
    vaccineName: '',
    dateGiven: '',
    validTill: '',
    vet: '',
    certificate: '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          photo: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificateFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          certificate: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/animals`, formData);
      
      if (formData.vaccinated && formData.vaccineName) {
        await axios.post(`${API}/vaccines`, {
          animalId: response.data.id,
          vaccineName: formData.vaccineName,
          dateGiven: formData.dateGiven,
          validTill: formData.validTill,
          vet: formData.vet,
          certificate: formData.certificate,
        });
      }
      
      navigate('/');
    } catch (error) {
      console.error('Error adding animal:', error);
      alert('Failed to add animal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden">
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-8">
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-xl shadow-lg">
                  <PawPrint className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-extrabold text-white font-heading" data-testid="add-animal-title">
                    Add New Animal
                  </h1>
                  <p className="text-white/90 font-medium">Register a stray animal in the system</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Species *</label>
                  <select
                    name="species"
                    value={formData.species}
                    onChange={handleChange}
                    required
                    data-testid="input-species"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    <option value="Dog">Dog</option>
                    <option value="Cat">Cat</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="input-name"
                    placeholder="Enter animal name"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    data-testid="input-gender"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Colour *</label>
                  <input
                    type="text"
                    name="colour"
                    value={formData.colour}
                    onChange={handleChange}
                    required
                    data-testid="input-colour"
                    placeholder="e.g., Brown, White, Black"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Area *</label>
                  <input
                    type="text"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    required
                    data-testid="input-area"
                    placeholder="e.g., Bandra, Andheri"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Temperament *</label>
                  <select
                    name="temperament"
                    value={formData.temperament}
                    onChange={handleChange}
                    required
                    data-testid="input-temperament"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    <option value="Friendly">Friendly</option>
                    <option value="Aggressive">Aggressive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Photo Attachment</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  data-testid="input-photo"
                  className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-hover"
                />
                {photoFile && (
                  <p className="text-sm text-green-600 mt-2 font-medium">✓ {photoFile.name} selected</p>
                )}
              </div>

              <div>
                <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  data-testid="input-remarks"
                  rows="4"
                  placeholder="Any additional notes about the animal..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none resize-none"
                />
              </div>

              <div className="border-t-2 border-orange-100 pt-6">
                <h3 className="text-lg font-bold text-text-primary mb-4 font-heading">Owner Details (For Pet Animals)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Tower</label>
                    <select
                      name="ownerTower"
                      value={formData.ownerTower}
                      onChange={handleChange}
                      data-testid="input-owner-tower"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                    >
                      <option value="">Select Tower</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Flat No</label>
                    <input
                      type="text"
                      name="ownerFlatNo"
                      value={formData.ownerFlatNo}
                      onChange={handleChange}
                      data-testid="input-owner-flat"
                      placeholder="e.g., 101, 202"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Owner Name</label>
                    <input
                      type="text"
                      name="ownerName"
                      value={formData.ownerName}
                      onChange={handleChange}
                      data-testid="input-owner-name"
                      placeholder="Owner's full name"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Owner Contact</label>
                    <input
                      type="tel"
                      name="ownerContact"
                      value={formData.ownerContact}
                      onChange={handleChange}
                      data-testid="input-owner-contact"
                      placeholder="+91 98765 43210"
                      className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-orange-100 bg-orange-50 cursor-pointer hover:border-primary transition-all">
                  <input
                    type="checkbox"
                    name="sterilized"
                    checked={formData.sterilized}
                    onChange={handleChange}
                    data-testid="input-sterilized"
                    className="w-5 h-5 text-primary rounded focus:ring-4 focus:ring-orange-100"
                  />
                  <span className="font-bold text-text-primary">Sterilized</span>
                </label>

                <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-orange-100 bg-orange-50 cursor-pointer hover:border-primary transition-all">
                  <input
                    type="checkbox"
                    name="vaccinated"
                    checked={formData.vaccinated}
                    onChange={handleChange}
                    data-testid="input-vaccinated"
                    className="w-5 h-5 text-primary rounded focus:ring-4 focus:ring-orange-100"
                  />
                  <span className="font-bold text-text-primary">Vaccinated</span>
                </label>
              </div>

              {formData.vaccinated && (
                <div className="border-t-2 border-green-100 pt-6 bg-green-50 p-6 rounded-xl">
                  <h3 className="text-lg font-bold text-text-primary mb-4 font-heading flex items-center gap-2">
                    <Syringe className="w-5 h-5 text-green-600" />
                    Vaccine Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Vaccine Name *</label>
                      <input
                        type="text"
                        name="vaccineName"
                        value={formData.vaccineName}
                        onChange={handleChange}
                        required={formData.vaccinated}
                        data-testid="input-vaccine-name"
                        placeholder="e.g., Rabies"
                        className="w-full h-12 px-4 rounded-xl border-2 border-green-200 bg-white focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Date Given *</label>
                      <input
                        type="date"
                        name="dateGiven"
                        value={formData.dateGiven}
                        onChange={handleChange}
                        required={formData.vaccinated}
                        data-testid="input-date-given"
                        className="w-full h-12 px-4 rounded-xl border-2 border-green-200 bg-white focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Valid Till *</label>
                      <input
                        type="date"
                        name="validTill"
                        value={formData.validTill}
                        onChange={handleChange}
                        required={formData.vaccinated}
                        data-testid="input-valid-till"
                        className="w-full h-12 px-4 rounded-xl border-2 border-green-200 bg-white focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Vet Name *</label>
                      <input
                        type="text"
                        name="vet"
                        value={formData.vet}
                        onChange={handleChange}
                        required={formData.vaccinated}
                        data-testid="input-vet"
                        placeholder="Veterinarian name"
                        className="w-full h-12 px-4 rounded-xl border-2 border-green-200 bg-white focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Certificate Attachment</label>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleCertificateChange}
                        data-testid="input-certificate"
                        className="w-full h-12 px-4 rounded-xl border-2 border-green-200 bg-white focus:border-green-500 focus:ring-4 focus:ring-green-100 transition-all outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700"
                      />
                      {certificateFile && (
                        <p className="text-sm text-green-600 mt-2 font-medium">✓ {certificateFile.name} attached</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-button"
                  className="flex-1 bg-primary text-white shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8 py-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5 inline mr-2" />
                  {loading ? 'Saving...' : 'Save Animal'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  data-testid="cancel-button"
                  className="bg-white text-primary border-2 border-orange-100 hover:border-primary hover:bg-orange-50 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                >
                  <X className="w-5 h-5 inline mr-2" />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AddAnimal;